// Client-side password feedback, matched to the backend's Django validators
// (config/settings/common.py → AUTH_PASSWORD_VALIDATORS, all default config):
//   - MinimumLengthValidator   → at least 8 characters
//   - NumericPasswordValidator → not entirely numeric
//   - CommonPasswordValidator  → not a well-known common password
//   - UserAttributeSimilarityValidator → not too similar to email/name
//     (fires on reset/change, where a user instance exists — not at signup)
//
// Only the deterministic rules (length, not-numeric) gate submission; the
// rest are advisory and the backend stays authoritative (its specific 400
// message is surfaced via getErrorMessage if a password still slips through).

export const MIN_PASSWORD_LENGTH = 8

// A small subset of the backend's common-password list — enough to nudge the
// most frequent weak choices without shipping the full ~20k-entry list.
const COMMON_PASSWORDS = new Set([
  'password',
  'password1',
  'password123',
  '12345678',
  '123456789',
  '1234567890',
  'qwerty',
  'qwertyuiop',
  'abc12345',
  'iloveyou',
  'admin',
  'welcome',
  'letmein',
  'monkey',
  'football',
  'starwars',
  'whatever',
  'trustno1'
])

const CHAR_CLASSES = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/]

const isNumericOnly = (password) => /^\d+$/.test(password)

const isCommon = (password) => COMMON_PASSWORDS.has(password.toLowerCase())

// Strength is advisory only: 1 = Weak, 2 = Fair, 3 = Strong.
const strengthFor = (password, valid) => {
  if (!valid) return 1
  const variety = CHAR_CLASSES.filter((re) => re.test(password)).length
  let points = 0
  if (password.length >= 10) points += 1
  if (password.length >= 14) points += 1
  if (variety >= 2) points += 1
  if (variety >= 3) points += 1
  if (points <= 1) return 1
  if (points === 2) return 2
  return 3
}

const STRENGTH_LABELS = { 1: 'Weak', 2: 'Fair', 3: 'Strong' }

// Evaluate a password into the rule states + strength used by <PasswordStrength>.
export const evaluatePassword = (password = '') => {
  const hasInput = password.length > 0
  const lengthMet = password.length >= MIN_PASSWORD_LENGTH
  const notNumericMet = hasInput && !isNumericOnly(password)
  const notCommonMet = hasInput && !isCommon(password)
  const valid = lengthMet && notNumericMet
  const level = strengthFor(password, valid)

  return {
    valid,
    strength: {
      level,
      label: STRENGTH_LABELS[level]
    },
    rules: [
      { key: 'length', label: `Use at least ${MIN_PASSWORD_LENGTH} characters`, met: lengthMet },
      { key: 'notNumeric', label: 'Don’t use only numbers', met: notNumericMet },
      { key: 'notCommon', label: 'Avoid common passwords', met: notCommonMet }
    ]
  }
}

// react-hook-form `validate` for a password field: gates on the deterministic
// backend rules, returning a message string on failure (true otherwise).
// Empty is left to the field's `required` rule.
export const validatePasswordField = (value = '') => {
  if (!value) return true
  if (value.length < MIN_PASSWORD_LENGTH)
    return `Use at least ${MIN_PASSWORD_LENGTH} characters.`
  if (isNumericOnly(value)) return 'Don’t use only numbers.'
  return true
}

// Convenience predicate for gating a submit button on the live value.
export const isPasswordValid = (password = '') =>
  evaluatePassword(password).valid
